import type { LalamoveSpecialRequestOption } from '../../lib/deliveryArrangements';

type LalamoveSpecialRequestsPickerProps = {
  options: LalamoveSpecialRequestOption[];
  selected: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
};

function toggleSelection(
  current: string[],
  name: string,
  options: LalamoveSpecialRequestOption[],
): string[] {
  const target = options.find((item) => item.name === name);
  if (!target) return current;
  if (current.includes(name)) {
    return current.filter((item) => item !== name);
  }
  if (target.parentType && target.maxSelection === 1) {
    const siblings = options
      .filter((item) => item.parentType === target.parentType)
      .map((item) => item.name);
    return [...current.filter((item) => !siblings.includes(item)), name];
  }
  return [...current, name];
}

export function LalamoveSpecialRequestsPicker({
  options,
  selected,
  onChange,
  disabled,
}: LalamoveSpecialRequestsPickerProps) {
  if (options.length === 0) {
    return (
      <p className="delivery-form-card__hint" style={{ margin: 0 }}>
        No additional services for this vehicle. Helper and Secure Zone are usually available on{' '}
        <strong>1.7m Van</strong> or larger — change the vehicle above, then refresh the page if
        options still do not appear.
      </p>
    );
  }

  const groups = new Map<string, LalamoveSpecialRequestOption[]>();
  const standalone: LalamoveSpecialRequestOption[] = [];
  for (const option of options) {
    if (option.parentType) {
      const list = groups.get(option.parentType) ?? [];
      list.push(option);
      groups.set(option.parentType, list);
    } else {
      standalone.push(option);
    }
  }

  const renderOption = (option: LalamoveSpecialRequestOption) => {
    const checked = selected.includes(option.name);
    return (
      <label key={option.name} className="delivery-special-request">
        <input
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={() => onChange(toggleSelection(selected, option.name, options))}
        />
        <span className="delivery-special-request__copy">
          <strong>{option.description}</strong>
          <span className="delivery-special-request__code">{option.name}</span>
        </span>
      </label>
    );
  };

  return (
    <div className="delivery-special-requests">
      {[...groups.entries()].map(([parentType, groupOptions]) => (
        <fieldset key={parentType} className="delivery-special-requests__group">
          <legend>{parentType}</legend>
          {groupOptions.length > 1 ? (
            <p className="delivery-form-card__hint">Select up to 1 in this group.</p>
          ) : null}
          {groupOptions.map(renderOption)}
        </fieldset>
      ))}
      {standalone.length > 0 ? (
        <fieldset className="delivery-special-requests__group">
          <legend>Other services</legend>
          {standalone.map(renderOption)}
        </fieldset>
      ) : null}
    </div>
  );
}
